import { inject, Injectable } from '@angular/core';

import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';

import { Observable, throwError } from 'rxjs';

import { catchError } from 'rxjs/operators';

import {
  WcgToastBoxShadow,
  WcgToastConfig,
  WcgToastPanelSize,
  WcgToastService,
} from '@wcg/frontend-framework';

import { CUSTOM_ERROR_HANDLING, OVERRIDE_ERROR_MESSAGE } from '@shared/http';
import { ERM_ICONS_ENUM } from '@erm/cluster';

import { NewRelicService } from '../services/newrelic.service';
import { httpErrorMessage } from '../utils/http-error.util';

import { fetchErrorMessage } from '../constants';

import { bodyContainsSpecialCharacters, isGatewayBlocked } from '../utils';
import { ErrorMessage } from '../types';

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  private readonly _newRelic = inject(NewRelicService);
  private readonly _wcgToastMessage = inject(WcgToastService);

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        // Let MaintenanceInterceptor handle 423 errors
        if (error.status === 423) {
          return throwError(() => error);
        }

        // ERM-4993 Added to allow silent error handling for specific requests instead of popup
        const customHandling = request.context.get(CUSTOM_ERROR_HANDLING);
        if (customHandling === 'silent-mode') {
          return throwError(() => error);
        }
        if (request.url.includes('/auth/admin/user/') && error.status === 409) {
          return throwError(() => error);
        }
        // Invite conflict: show the short `message` field in the snackbar.
        if (error.status === 409 && error.error?.conflict_type) {
          const uiMessage =
            error.error?.message ||
            error.error?.detail ||
            'Invite blocked due to a conflict.';
          this._showErrorToast(uiMessage, {
            duration: 5000,
            height: WcgToastPanelSize.XXLarge,
            width: WcgToastPanelSize.XXXLarge,
            panelClass: 'warning-snackbar',
          });
          return throwError(() => ({
            message: uiMessage,
            detail: error.error?.detail,
            conflict_type: error.error.conflict_type,
          }));
        }
        this._newRelic.trackEvent('Error', { error });
        this.trackSpecificError(error, request);
        // A 409 is a conflict with current server state ("you already have an
        // export in progress", "already has access", …), not a generic
        // failure: the detail is specific enough that the feature should show
        // it in context. Pass the raw response through so callers can read
        // `status`/`error.detail` — features owning a 409 must surface their
        // own message; there is no automatic snackbar for it.
        if (error.status === 409) {
          return throwError(() => error);
        }
        // ERM-AUTH-403: the authenticated Okta user has an invalid email
        // domain or an invalid role assigned to their account. No snackbar —
        // AuthService redirects to a dedicated sign-in error page that
        // renders `message`/`detail` itself.
        const errorCode = error.error?.code?.toLowerCase();

        if (
          error.status === 403 &&
          (errorCode === 'erm-auth-403' || errorCode === '404')
        ) {
          return throwError(() => error.error);
        }
        // A WAF block loses its headers once it collapses to a CORS-masked
        // status 0 - the request body is the only signal left, so 403 also
        // confirms via isGatewayBlocked (content-type) while status 0 relies
        // on bodyContainsSpecialCharacters alone. Status/method narrow first;
        // the recursive body scan only runs once those cheap checks pass.
        if (
          ['PUT', 'POST', 'DELETE'].includes(request.method) &&
          (error.status === 403
            ? isGatewayBlocked(error)
            : error.status === 0) &&
          bodyContainsSpecialCharacters(request.body)
        ) {
          const message = httpErrorMessage('contentBlocked');

          this._showErrorToast(message, {
            duration: 5000,
            height: WcgToastPanelSize.XXLarge,
            width: WcgToastPanelSize.XXXLarge,
            panelClass: 'error-snackbar',
          });

          return throwError(() => message);
        }

        if (
          error.status === 403 &&
          ['PUT', 'POST', 'DELETE'].includes(request.method)
        ) {
          const message =
            error?.error?.message || httpErrorMessage('forbidden', 'perform');

          this._showErrorToast(message, {
            duration: 5000,
            height: WcgToastPanelSize.XXLarge,
            width: WcgToastPanelSize.XXXLarge,
            panelClass: 'error-snackbar',
          });

          return throwError(() => message);
        } else if (error.status === 403) {
          // The rethrow below still happens either way, so a feature that
          // suppresses this toast is taking over the messaging, not losing it.
          if (!isSelfHandledForbidden(request.url)) {
            this._showErrorToast(
              error?.error?.detail || httpErrorMessage('forbidden'),
              {
                duration: 5000,
                height: WcgToastPanelSize.XXLarge,
                width: WcgToastPanelSize.XXXLarge,
                panelClass: 'error-snackbar',
              },
            );
          }
          return throwError(() => httpErrorMessage('forbidden'));
        } else if (error.status === 404) {
          this._showErrorToast(
            error?.error?.detail || httpErrorMessage('notFound'),
            {
              duration: 5000,
              height: WcgToastPanelSize.XXLarge,
              width: WcgToastPanelSize.XXXLarge,
              panelClass: 'error-snackbar',
            },
          );
          return throwError(() => httpErrorMessage('notFound'));
        }

        // Address add/edit: do not show snackbar; address wrapper shows inline "Your Entry" vs "Suggested" form (no banner).
        // Match any status when `detail.corrected_address` is present (some environments use 400 instead of 422).
        const addressDetail = error.error?.detail;
        if (
          addressDetail &&
          typeof addressDetail === 'object' &&
          addressDetail !== null &&
          'corrected_address' in addressDetail
        ) {
          const d = addressDetail as {
            message?: unknown;
            corrected_address?: unknown;
          };
          const errorPayload = {
            code: error.error?.code,
            message:
              typeof d.message === 'string' ? d.message : error.error?.message,
            corrected_address: d.corrected_address,
          };
          return throwError(() => errorPayload);
        }

        // Escape the snackbar for specialized error messages
        if (
          error.status === 400 &&
          error.error &&
          error.error.module_title &&
          error.error.code === 'required_validation'
        ) {
          const errorMessage = `${error.error.module_title}`;
          return throwError(() => errorMessage);
        }

        if (
          error.status === 400 &&
          error.error &&
          error.error.code === 'missing_sme_qc_marks'
        ) {
          const sections = error.error.sections;
          const docs = error.error.docs;
          const errorMessage: ErrorMessage = {
            message: 'Please complete the required fields and try again.',
            docs: docs,
            sections: sections,
            code: error.error.code,
          };
          return throwError(() => errorMessage);
        }

        if (
          error.status === 400 &&
          error.error &&
          error.error.code === 'tasks_not_completed'
        ) {
          const errorMessage: ErrorMessage = {
            message: error.error.message,
            section: error.error.section ?? null,
            redirect: error.error.redirect,
            code: error.error.code,
          };
          return throwError(() => errorMessage);
        }
        if (
          error.status === 400 &&
          error.error &&
          error.error.code === 'required_validation'
        ) {
          const errorMessage: ErrorMessage = {
            message: error.error.message,
            section: error.error.section ?? null,
            redirect: error.error.redirect,
            code: error.error.code,
          };
          return throwError(() => errorMessage);
        }

        if (error.status === 500) {
          const message = fetchErrorMessage(
            error.status,
            request.context.get(OVERRIDE_ERROR_MESSAGE),
          );
          this._showErrorToast(message, {
            duration: 5000,
            height: WcgToastPanelSize.XXLarge,
            width: WcgToastPanelSize.XXXLarge,
            panelClass: 'error-snackbar m-6!',
          });
          return throwError(() => ({ message, code: error.error.code }));
        }

        if (error.status === 0) {
          let message = fetchErrorMessage(
            error.status,
            request.context.get(OVERRIDE_ERROR_MESSAGE),
          );

          // ErrorEvent is a client-side error
          if (
            !message ||
            (!message.length && error.error instanceof ErrorEvent)
          ) {
            message = 'Unable to connect to the server.';
          }

          this._showErrorToast(message, {
            duration: 5000,
            height: WcgToastPanelSize.XXLarge,
            width: WcgToastPanelSize.XXXLarge,
            panelClass: 'error-snackbar',
          });

          throw new Error(message);
        }

        let errorMessage = 'An unknown error occurred!';

        if (error && error.error instanceof ErrorEvent === false) {
          let message =
            error.error?.detail ||
            error.error?.message ||
            error.message ||
            'Error';
          try {
            // Server-side errors
            if (typeof message === 'string') {
              const newMessage = message.replace(/'/g, '"');
              const errorMsg = newMessage.includes('{')
                ? JSON.parse(newMessage)
                : { message: newMessage };
              const propertiesToUpdate = ['duplicate_file', 'extension'];

              propertiesToUpdate.forEach((property) => {
                if (errorMsg && errorMsg[property]) {
                  message = errorMsg[property];
                }
              });
            }
            if (typeof message === 'object' || !message) {
              message = 'Something went wrong with the server.';
            }
          } catch (e) {
            console.error('error', e);
          }
          errorMessage = `Error: ${message}`;
        }

        if (
          error.url &&
          !DO_NOT_INTERCEPT_LIST.has(errorMessage) &&
          !shouldSkipInterception(request.url, errorMessage) &&
          !DO_NOT_INTERCEPT_REGEX_LIST.some((regex) => regex.test(errorMessage))
        ) {
          this._showErrorToast(errorMessage, {
            duration: 5000,
            height: WcgToastPanelSize.XXLarge,
            width: WcgToastPanelSize.XXXLarge,
            panelClass: 'error-snackbar',
          });
        }
        /**
         * @NOTE:- If none of the interception conditions are met, the error is thrown as a new Error with the constructed errorMessage.
         */
        return throwError(() => new Error(errorMessage));
      }),
    );
  }

  trackSpecificError(error: HttpErrorResponse, request: HttpRequest<any>) {
    switch (error.status) {
      case 0:
        this._newRelic.trackEvent('ServerConnectionError', {
          message: 'Server not responding',
          method: request.method,
        });
        break;
      case 401:
        this._newRelic.trackError(error, {
          context: 'Unauthorized access',
          url: request.url,
        });
        this._newRelic.trackEvent('AuthenticationError', {
          endpoint: request.url,
          message: 'Unauthorized access',
        });
        break;
      case 403:
        if (error?.error?.detail) {
          this._newRelic.trackEvent('Forbidden Error', {
            endpoint: request.url,
            message: error?.error?.detail,
            method: request.method,
          });
          this._newRelic.trackError(error, {
            context: 'Forbidden Error',
            url: request.url,
          });
        } else {
          this._newRelic.trackEvent('AuthorizationError', {
            endpoint: request.url,
            message: 'Forbidden access',
            method: request.method,
          });
          this._newRelic.trackError(error, {
            context: 'Authorization Error',
            url: request.url,
          });
        }
        break;
      case 404:
        this._newRelic.trackEvent('NotFoundError', {
          endpoint: request.url,
          message: 'Resource not found',
        });
        break;
      case 400:
        this._newRelic.trackEvent('BadRequestError', {
          message: error?.error?.detail || 'Bad request',
          validationErrors: JSON.stringify(error.error),
        });
        break;
      case 500:
        this._newRelic.trackEvent('InternalServerError', {
          message: 'Internal server error',
          method: request.method,
          url: request.url,
        });
        break;
      case 504:
        this._newRelic.trackEvent('GatewayTimeoutError', {
          message: 'Gateway timeout',
          method: request.method,
          url: request.url,
        });
        break;
    }
  }

  private _showErrorToast(message: string, config: WcgToastConfig) {
    this._wcgToastMessage.open({
      data: {
        message: message,
      },
      duration: config.duration,
      height: config.height,
      width: config.width,
      toastType: 'error',
      panelClass: `p-8! gap-6! ${config.panelClass}`,
      toastMessageClass: 'body-large mr-1!',
      icon: ERM_ICONS_ENUM.ERROR,
      iconPosition: 'front',
      horizontalPosition: 'center',
      boxShadow: WcgToastBoxShadow.XLarge,
    });
  }
}

export const DO_NOT_INTERCEPT_LIST = new Set<string>([
  'Error: No Match found. Please try again',
  'Error: You already have access to this Site',
  'Error: You already have access to this Study',
  'Error: No match found. Please try again. If you need help, please contact WCG Client Care.',
  'Error: You have already requested access to this Site. You will receive an email notification once it has been accepted or denied.',
  'Error: You have already requested access to this Study. You will receive an email notification once it has been accepted or denied.',
  'Error: More than one record matches. Please enter a different search criteria and try again. If you need help, please contact WCG Client Care.',
  'Error: Please complete the required fields and try again. If you need help, please contact WCG Client Care.',
  'Error: MAINTENANCE_MODE',
  // Bug 1 — updated messages matching acceptance criteria wording
  'Error: You already have access to this Study.',
  'Error: You already have access to this Site.',
  // Bug 2 — new study-level access check message
  'Error: You already have access to this Site through your access to the Study.',
  // Bug 3 — updated pending request messages matching acceptance criteria wording
  'Error: You already have a pending request for this Study.',
  'Error: You already have a pending request for this Site.',
  'Error: body: value_error: Value error, Invalid message link',
]);

export const DO_NOT_INTERCEPT_REGEX_LIST = [
  /^Error: Legacy ID: \d+ does not exists$/,
];

export const DO_NOT_INTERCEPT_URL_ERROR_LIST = new Map<string, string>([
  [
    '/workspace/questionnaire/',
    'Error: Insufficient permissions to perform action',
  ],
  ['/document/workspace/', 'Error: Insufficient permissions to perform action'],
]);

/**
 * URLs whose 403 the calling feature handles itself with a more specific
 * message, so the generic permission toast is suppressed for them.
 *
 * The submission sites reload is the case this exists for: a site user who
 * removes the site they came in through loses access as a *result* of their
 * own edit. "Insufficient permissions to perform action" reads as though the
 * removal was rejected, when it actually succeeded - `SitesSectionComponent`
 * says what really happened and navigates the user out.
 */
export const SELF_HANDLED_FORBIDDEN_URL_LIST = [
  /\/workspace\/[^/]+\/sites\/?(\?|$)/,
];

export function isSelfHandledForbidden(url: string): boolean {
  return SELF_HANDLED_FORBIDDEN_URL_LIST.some((pattern) => pattern.test(url));
}

// Characters with confirmed WAF-trigger evidence only: the e2e guardrail
// suite names parentheses/brackets, and HttpRequestDataInterceptor's own
// comment names raw angle brackets as an XSS-rule trigger. Deliberately
// excludes ordinary punctuation like '@', '!', or apostrophes - those appear
// in everyday content (emails, emphasis, contractions) and would misattribute
// an unrelated 403 (e.g. a genuine authorization denial that happens to
// render as HTML) to a content block instead of reporting access was denied.

export function shouldSkipInterception(url: string, error: string): boolean {
  for (const [path, message] of DO_NOT_INTERCEPT_URL_ERROR_LIST.entries()) {
    if (url.includes(path) && error === message) {
      return true;
    }
  }
  return false;
}
